// Phase 9 /models/logs — Phase 7 imported telemetry logs.
//
// List + format chips, upload via multipart, preview modal with a small
// line chart of the first frame block.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { logs as logsApi } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

const FORMAT_META = {
    csv_telemetry: { label: "CSV", class: "bg-ux-bg-2 text-ux-text-2 border-ux-border-2" },
    px4_ulog: { label: "PX4 ULog", class: "bg-ux-accent/20 text-ux-accent border-ux-accent/40" },
    ardupilot_bin: { label: "ArduPilot .bin", class: "bg-ux-warning-soft text-ux-warning border-ux-warning/40" },
    mavlink_tlog: { label: "MAVLink tlog", class: "bg-ux-success/20 text-ux-success border-ux-success/40" },
};

export default function LogsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [preview, setPreview] = useState(null); // { id, frames }
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const data = await logsApi.list();
            setRows(Array.isArray(data) ? data : data?.items || []);
            setErr("");
        } catch (e) {
            setErr(formatApiError(e, "Failed to load logs"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const upload = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            await logsApi.upload(fd);
            toast.success(`Imported ${file.name}`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e, "Import failed"));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const showPreview = async (l) => {
        try {
            const frames = await logsApi.preview(l.id || l.log_id);
            setPreview({ id: l.id || l.log_id, name: l.name, frames });
        } catch (e) {
            toast.error(formatApiError(e, "Preview failed"));
        }
    };

    return (
        <div data-testid="logs-page">
            <PageHeader
                title="Imported Logs"
                subtitle="Canonicalised telemetry logs — CSV, PX4 ULog, ArduPilot .bin, MAVLink tlog."
                actions={[
                    <label
                        key="upload"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] bg-ux-accent text-black cursor-pointer"
                        data-testid="logs-upload-label"
                    >
                        <Upload className="w-4 h-4" />
                        {uploading ? "Uploading…" : "Upload log"}
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv,.ulg,.bin,.tlog"
                            className="hidden"
                            onChange={(e) => upload(e.target.files?.[0])}
                            data-testid="logs-upload-input"
                        />
                    </label>,
                ]}
            />

            {err && (
                <div className="mb-4 p-3 rounded-md border border-ux-danger/40 bg-ux-danger/10 text-ux-danger text-[13px]">
                    {err}
                </div>
            )}

            <div className="rounded-lg border border-ux-border-1 overflow-hidden">
                <table className="w-full text-[13px]" data-testid="logs-table">
                    <thead className="bg-ux-bg-1 text-ux-text-3 text-[11px] uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-3 py-2">Name</th>
                            <th className="text-left px-3 py-2">Format</th>
                            <th className="text-left px-3 py-2">Samples</th>
                            <th className="text-left px-3 py-2">Duration</th>
                            <th className="text-left px-3 py-2">SHA256</th>
                            <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="px-3 py-8 text-center text-ux-text-3">
                                    loading…
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-3 py-8 text-center text-ux-text-3">
                                    No logs yet — upload one to get started.
                                </td>
                            </tr>
                        )}
                        {rows.map((l) => {
                            const id = l.id || l.log_id;
                            const meta = FORMAT_META[l.format] || {
                                label: l.format || "—",
                                class: "bg-ux-bg-2 text-ux-text-2 border-ux-border-2",
                            };
                            return (
                                <tr
                                    key={id}
                                    className="border-t border-ux-border-1 hover:bg-ux-bg-1"
                                    data-testid={`logs-row-${id}`}
                                >
                                    <td className="px-3 py-2">
                                        <div className="inline-flex items-center gap-2">
                                            <FileText className="w-3.5 h-3.5 text-ux-text-3" />
                                            <span className="text-ux-text-1">{l.name || id}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span
                                            className={[
                                                "inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border",
                                                meta.class,
                                            ].join(" ")}
                                            data-testid={`logs-format-${id}`}
                                        >
                                            {meta.label}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-2">
                                        {l.sample_count ?? l.n_samples ?? "—"}
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-2">
                                        {l.duration_s ? `${l.duration_s.toFixed(1)} s` : "—"}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-ux-text-3">
                                        {(l.sha256 || "").slice(0, 10)}…
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            onClick={() => showPreview(l)}
                                            className="text-[12px] text-ux-accent hover:underline"
                                            data-testid={`logs-preview-${id}`}
                                        >
                                            preview
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {preview && (
                <div
                    className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setPreview(null);
                    }}
                >
                    <div
                        className="w-full max-w-3xl bg-ux-bg-1 rounded-lg border border-ux-border-1 max-h-[85vh] flex flex-col"
                        data-testid="logs-preview-modal"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-ux-border-1">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                    Preview
                                </div>
                                <div className="text-[14px] text-ux-text-1 font-mono">
                                    {preview.name || preview.id}
                                </div>
                            </div>
                            <button
                                onClick={() => setPreview(null)}
                                className="text-ux-text-3 hover:text-ux-text-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <pre
                            className="flex-1 overflow-auto p-4 text-[11px] text-ux-text-2 font-mono"
                            data-testid="logs-preview-body"
                        >
                            {JSON.stringify(preview.frames, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}
